import { type MutableRefObject, type Ref, type RefCallback, useMemo } from 'react';

function toFnRef<T>(ref?: Ref<T> | null): RefCallback<T> | null {
    if (typeof ref === 'function') {
        return ref;
    }

    if (!ref) {
        return null;
    }

    return (value: T) => {
        (ref as MutableRefObject<T | null>).current = value;
    };
}

function mergeRefs<T>(refA?: Ref<T> | null, refB?: Ref<T> | null): RefCallback<T> {
    const a = toFnRef(refA);
    const b = toFnRef(refB);
    return (value: T | null) => {
        if (a) {
            a(value);
        }
        if (b) {
            b(value);
        }
    };
}

/**
 * Create and returns a single callback ref composed from two other Refs.
 *
 * ```tsx
 * function Button({ ref, ...props }: ButtonProps & { ref?: React.Ref<HTMLButtonElement> }) {
 *   const attachRef = useRef<HTMLButtonElement>(null);
 *   const mergedRef = useMergedRefs(ref, attachRef);
 *
 *   return <button ref={mergedRef} {...props}/>
 * }
 * ```
 */
export function useMergedRefs<T>(refA?: Ref<T> | null, refB?: Ref<T> | null): RefCallback<T> {
    return useMemo(() => mergeRefs(refA, refB), [refA, refB]);
}

useMergedRefs.mergeRefs = mergeRefs;
